<script setup lang="ts">
import { computed, onMounted, toRef, watch, ref } from "vue";
import { RouterLink, useRouter, type LocationAsRelativeRaw } from "vue-router";
import { storeToRefs } from "pinia";
import { useAccountStore } from "@/stores/accountStore";
import { useSidebarStore } from "@/stores/sidebarStore";
import { useImage } from "@/composables/useImage";

import LoaderComponentVue from "@/components/LoaderComponent.vue";

const accountStore = useAccountStore();
const { init } = useSidebarStore();
const { currentAccount } = storeToRefs(accountStore);
const { sidebarLinks } = storeToRefs(useSidebarStore());

const router = useRouter();

const accountName = computed(() => currentAccount.value.content?.name ?? "-");
const accountLogo = computed(() => currentAccount.value.content?.accountLogoUrl);
const accountLoaded = computed(() => !currentAccount.value.isLoading && currentAccount.value.content);

watch(
  () => currentAccount.value.content,
  () => loadImage()
);

const { isLoading: imageLoading, loadImage, imgSrc } = useImage(
  accountLogo,
  toRef('/images/icon-company.svg')
);

onMounted(async () => {
  await init();
});

function isRouteToBeMarkedAsActive(to: LocationAsRelativeRaw) {
  const currentRoute = router.currentRoute.value;
  return currentRoute.meta["setRouteAsActive"] == to.name;
}
</script>

<template>
  <nav class="sidebar-nav" v-if="accountLoaded">
    <div class="sidebar-nav__logo _use-m-b">
        <div class="sidebar-nav__logo__img">
          <LoaderComponentVue v-if="imageLoading" />
          <img v-if="!imageLoading" :src="imgSrc" />
        </div>
        <div class="sidebar-nav__logo__name _use-m-t _sm">{{ accountName }}</div>
        <div v-if="currentAccount.content?.isIndividualLicenseExists === true">
          <span class="tag _style-f">INDIVIDUAL</span>
        </div>
    </div>
    <ul>
      <li v-for="link in sidebarLinks" :key="link.text">
        <RouterLink v-if="link.routeTo" :to="link.routeTo"
          :class="{ 'router-link-active': isRouteToBeMarkedAsActive(link.routeTo) }">
          {{ link.text }}
          <div v-if="link.badge && parseInt(link.badge, 10) > 0" class="tooltip _use-hover">
            <div class="tooltip__control">
              <div class="badge">{{ link.badge }}</div>
            </div>
            <div class="tooltip__text">Number of users with pending requests</div>
          </div>
        </RouterLink>
        <a v-else :href="link.href" :target="(link.inNewTab ? '_blank' : undefined)">
          {{ link.text }}
          <div v-if="link.badge" class="tooltip _use-hover">
            <div class="tooltip__control">
              <div class="badge">12</div>
            </div>
            <div class="tooltip__text">Number of users with pending requests</div>
          </div>
        </a>
      </li>
    </ul>
  </nav>
</template>
